'use client';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Shield, Lock, Eye, FileText } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen flex flex-col font-sans overflow-hidden">
      {/* Background gradient matching inspiration - purple/blue theme */}
      <div className="absolute inset-0 bg-gradient-to-b from-accent/50 via-purple-100/30 to-background dark:from-purple-900/20 dark:via-purple-900/5 dark:to-background -z-10"></div>

      {/* Decorative elements */}
      <div className="absolute top-20 right-[10%] w-64 h-64 rounded-full bg-purple-200/20 dark:bg-purple-800/20 blur-3xl -z-10"></div>
      <div className="absolute bottom-20 left-[10%] w-96 h-96 rounded-full bg-indigo-200/20 dark:bg-indigo-800/20 blur-3xl -z-10"></div>

      <Navbar />

      <main className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-10 text-center">
            <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-blue-600/20 mb-4">
              <Shield className="h-8 w-8 text-blue-500" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
            <p className="text-lg text-muted-foreground">How we collect, use, and protect your information</p>
          </motion.div>

          <motion.div
            className="space-y-8 bg-card/50 backdrop-blur-sm rounded-xl p-8 border border-border shadow-xl"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}>
            <motion.div variants={fadeIn}>
              <div className="flex items-center mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-3">
                  <Lock className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">Information We Collect</h2>
              </div>
              <p className="text-muted-foreground mb-6 pl-12">
                We collect information that you provide directly to us, such as when you create an account, use our
                services, or contact support. This includes your name, email address, and any data you analyze using our
                platform.
              </p>
            </motion.div>

            <motion.div variants={fadeIn}>
              <div className="flex items-center mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-3">
                  <Eye className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">How We Use Your Information</h2>
              </div>
              <p className="text-muted-foreground mb-6 pl-12">
                We use your information to provide and improve our services, communicate with you, and ensure security
                of your account. Your data is used to train our AI models to deliver more accurate insights and
                personalized experiences.
              </p>
            </motion.div>

            <motion.div variants={fadeIn}>
              <div className="flex items-center mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-3">
                  <Shield className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">Data Security</h2>
              </div>
              <p className="text-muted-foreground mb-6 pl-12">
                We implement robust security measures to protect your personal information. This includes encryption,
                access controls, and regular security audits. We retain your data only as long as necessary to provide
                our services.
              </p>
            </motion.div>

            <motion.div variants={fadeIn}>
              <div className="flex items-center mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-3">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">Your Rights</h2>
              </div>
              <p className="text-muted-foreground mb-6 pl-12">
                You have the right to access, correct, or delete your personal information. You can also restrict or
                object to certain processing of your data. To exercise these rights, please contact us at
                support@slashml.com.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="mt-10 text-center text-muted-foreground text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}>
            <p>Last updated: February 15, 2025</p>
            <p className="mt-2">
              If you have any questions about our Privacy Policy, please{' '}
              <a
                href="mailto:support@slashml.com"
                className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                contact us
              </a>
              .
            </p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
